import type { PromoPricingTacticsModel } from '@/shared/generated'

import { useMemo } from 'react'
import { pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Spinner } from '@/shared/components/ui/spinner'
import { intlFormat } from '@/shared/tools/dates'
import {
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export const PricingTacticPromoView = ({
	settings,
}: {
	settings: PromoPricingTacticsModel['params']
}) => {
	const { t } = useTranslation()
	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:tactics_promo'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})

	const schema = schemaQuery.data
	const targetOptions = useMemo(
		() =>
			Object.fromEntries(
				prop('properties.params.properties.target.oneOf', schema).map(
					(s: any) => [s?.properties?.name?.const, s.title]
				)
			),
		[schema]
	)

	if (schemaQuery.isLoading) {
		return <Spinner className='opacity-50' />
	}

	return (
		<DescriptionList className='flex flex-wrap gap-4'>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('promo_period_start_date', {
						defaultValue: 'Start date',
					})}
				</DescriptionTerm>
				<DescriptionDetail className='font-semibold'>
					{settings.promo_start_date
						? intlFormat(new Date(settings.promo_start_date))
						: '-'}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('promo_period_end_date', {
						defaultValue: 'Finish date',
					})}
				</DescriptionTerm>
				<DescriptionDetail className='font-semibold'>
					{settings.promo_end_date
						? intlFormat(new Date(settings.promo_end_date))
						: '-'}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('promo_period_target', {
						defaultValue: 'Promo period target',
					})}
				</DescriptionTerm>
				<DescriptionDetail className='font-semibold'>
					{targetOptions[settings.target.name]}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('promo_period_target_value', {
						defaultValue: 'Promo period target value',
					})}
				</DescriptionTerm>
				<DescriptionDetail className='font-semibold'>
					{settings.target.value}
				</DescriptionDetail>
			</DescriptionGroup>
		</DescriptionList>
	)
}
